import { MAX_OTHER, OTHER_ID, type AnswerInput, type Lang, type ResolvedQuestion } from '@aera/schema';
import { Check } from 'lucide-react';
import { useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface AnswerProps {
  rq: ResolvedQuestion;
  value: AnswerInput;
  onChange: (next: AnswerInput, opts?: { debounce?: boolean }) => void;
  lang: Lang;
  /** Rendered as a short chip row inside a shared screen (the profile screen). */
  compact?: boolean;
  /** A pick was refused because the question's maximum is reached. */
  onLimit?: () => void;
}

export const emptyAnswer = (): AnswerInput => ({ selected: [], unsure: false, skipped: false });

/** A choice clears an earlier "not sure" or skip. */
export const fresh = (value: AnswerInput): AnswerInput => ({ ...value, unsure: false, skipped: false });

/** A tappable card at least 56 px tall; indigo border and a popped check when selected. */
export function OptionCard({
  role,
  checked,
  onClick,
  children,
  hotkey,
  id,
}: {
  role: 'radio' | 'checkbox';
  checked: boolean;
  onClick: () => void;
  children: ReactNode;
  hotkey?: number;
  id?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role={role}
      aria-checked={checked}
      onClick={onClick}
      className="option-card flex w-full items-center gap-3 rounded-2xl border border-hair bg-white px-4 py-3 text-left text-[16px] leading-snug hover:border-[#cfcfd6]"
    >
      <span className="flex-1">{children}</span>
      {checked ? (
        <span className="lp-pop flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo text-white" aria-hidden>
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      ) : (
        hotkey !== undefined && (
          <span className="hidden h-6 w-6 shrink-0 items-center justify-center rounded-md border border-hair text-[12px] text-ink-3 sm:flex" aria-hidden>
            {hotkey}
          </span>
        )
      )}
    </button>
  );
}

/** A compact pill for band rows and the profile screen's chip rows. */
export function Chip({
  role,
  checked,
  onClick,
  children,
}: {
  role: 'radio' | 'checkbox' | 'button';
  checked?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role={role === 'button' ? undefined : role}
      aria-checked={role === 'button' ? undefined : !!checked}
      onClick={onClick}
      className="chip inline-flex min-h-[44px] items-center rounded-full border border-hair bg-white px-4 py-2 text-[15px] leading-tight hover:border-[#cfcfd6]"
    >
      {/* One flex item, so tidy()'s nowrap span keeps its leading space. */}
      <span>{children}</span>
    </button>
  );
}

/**
 * The picks after typing into the inline Other field: text in it picks Other, an empty field unpicks it.
 * A single choice keeps only Other; a multi choice drops an exclusive pick ("none of these") for it.
 */
export function pickOther(selected: readonly string[], text: string, multi: boolean, isExclusive: (id: string) => boolean): string[] {
  const rest = selected.filter((s) => s !== OTHER_ID);
  if (!text.trim()) return rest;
  return multi ? [...rest.filter((s) => !isExclusive(s)), OTHER_ID] : [OTHER_ID];
}

/**
 * "其他" as a field the respondent types into directly, styled like the option cards (or a chip in a shared
 * screen's rows). It counts as picked once it has text; the number key for it focuses the field, and its
 * badge shows like an option card's while it is unpicked. A screen reader hears "selected" once it has text.
 */
export function OtherField({
  id,
  checked,
  value,
  onChange,
  label,
  compact,
  hotkey,
  lang,
}: {
  id?: string;
  checked: boolean;
  value: string;
  onChange: (v: string) => void;
  label: string;
  compact?: boolean;
  hotkey?: number;
  lang: Lang;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const pickedId = useId();
  return (
    <label
      id={id}
      data-checked={checked}
      // The number key clicks the label itself (and so may a tap on its padding): either focuses the field.
      onClick={(e) => {
        if (e.target === e.currentTarget) input.current?.focus();
      }}
      className={
        compact
          ? 'other-field chip inline-flex min-h-[44px] items-center rounded-full border border-hair bg-white px-4'
          : 'other-field option-card flex w-full items-center gap-3 rounded-2xl border border-hair bg-white px-4'
      }
    >
      <input
        ref={input}
        type="text"
        aria-label={label}
        placeholder={label}
        value={value}
        maxLength={MAX_OTHER}
        aria-describedby={checked ? pickedId : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`min-w-0 flex-1 bg-transparent outline-none placeholder:text-ink-3 ${compact ? 'w-[11em] py-2 text-[15px]' : 'py-3 text-[16px]'}`}
      />
      {checked && (
        <span id={pickedId} className="sr-only">
          {t('question.otherPicked', { lng: lang })}
        </span>
      )}
      {!compact &&
        (checked ? (
          <span className="lp-pop flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo text-white" aria-hidden>
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
        ) : (
          hotkey !== undefined && (
            <span className="hidden h-6 w-6 shrink-0 items-center justify-center rounded-md border border-hair text-[12px] text-ink-3 sm:flex" aria-hidden>
              {hotkey}
            </span>
          )
        ))}
    </label>
  );
}
